/**
 * E018-S014-T003: CrossDocAutomationService
 *
 * Service for Cross-Doc Automation Operations.
 * Enables reading source database rows and target database columns
 * across document boundaries with correct doc lifecycle management.
 *
 * Pattern: Follows CrossDocRelationService
 * - Permission check via GuardService
 * - docsService.open() → blockSuiteDoc.load() → waitForSyncReady() → release()
 *
 * Used by:
 * - AutomationConfigPanel (automation-panel.tsx)
 * - Input Tab (row preview)
 * - Output Tab (column mapping)
 */

import {
  createDatabaseBlockDataSource,
  DatabaseBlockDataSource,
  isDatabaseStorageModelReady,
  observeDatabaseStorageModelReady,
} from '@blocksuite/affine/blocks/database';
import type { DatabaseBlockModel } from '@blocksuite/affine-model';
import type { Store } from '@blocksuite/store';
import { Service } from '@toeverything/infra';

import type { DocsService } from '../../doc';
import type { GuardService } from '../../permissions';

// =============================================================================
// Errors
// =============================================================================

/**
 * Error when user lacks permission to read target doc
 */
export class PermissionDeniedError extends Error {
  constructor(
    public readonly docId: string,
    message?: string
  ) {
    super(message ?? `No read permission on document: ${docId}`);
    this.name = 'PermissionDeniedError';
  }
}

/**
 * Error when database is not found in document
 */
export class DatabaseNotFoundError extends Error {
  constructor(
    public readonly databaseId: string,
    public readonly docId: string
  ) {
    super(`Database ${databaseId} not found in document ${docId}`);
    this.name = 'DatabaseNotFoundError';
  }
}

// =============================================================================
// Types
// =============================================================================

/**
 * Column info for output mapping
 */
export interface CrossDocColumnInfo {
  id: string;
  name: string;
  type: string;
}

/**
 * Row data for input preview
 */
export interface CrossDocRowData {
  rowId: string;
  cells: Record<string, unknown>;
}

/**
 * Result of getting source rows
 */
export interface GetSourceRowsResult {
  success: boolean;
  rows?: CrossDocRowData[];
  columns?: CrossDocColumnInfo[];
  error?: Error;
}

/**
 * Result of getting target columns
 */
export interface GetTargetColumnsResult {
  success: boolean;
  columns?: CrossDocColumnInfo[];
  error?: Error;
}

/**
 * Internal: Opened doc with release function
 */
interface OpenedDoc {
  blockSuiteDoc: Store;
  release: () => void;
}

const DATABASE_STORAGE_READY_TIMEOUT_MS = 5000;

export async function waitForDatabaseStorageReady(
  model: DatabaseBlockModel,
  timeoutMs = DATABASE_STORAGE_READY_TIMEOUT_MS
): Promise<boolean> {
  if (isDatabaseStorageModelReady(model)) {
    return true;
  }

  return new Promise(resolve => {
    let completed = false;
    let cleanup: (() => void) | undefined;
    let timeout: ReturnType<typeof setTimeout>;

    const finish = (ready: boolean) => {
      if (completed) {
        return;
      }
      completed = true;
      clearTimeout(timeout);
      cleanup?.();
      resolve(ready);
    };

    timeout = setTimeout(() => {
      finish(isDatabaseStorageModelReady(model));
    }, timeoutMs);
    cleanup = observeDatabaseStorageModelReady(model, () => finish(true));
    if (completed) {
      cleanup();
    }
  });
}

// =============================================================================
// Service
// =============================================================================

export class CrossDocAutomationService extends Service {
  constructor(
    private readonly docsService: DocsService,
    private readonly guardService: GuardService
  ) {
    super();
  }

  // ===========================================================================
  // Permission Check
  // ===========================================================================

  /**
   * Check if user can read a document.
   * Uses Doc_Read permission for read-only operations.
   */
  async canReadDoc(docId: string): Promise<boolean> {
    try {
      // For read operations, we check Doc_Read permission
      // Note: If Doc_Read doesn't exist, fall back to checking if doc is accessible
      const canRead = await this.guardService.can('Doc_Read', docId);
      return canRead;
    } catch (e) {
      // If permission check fails, try to open doc as fallback
      // (some docs may be readable without explicit permission)
      console.warn(
        `[CrossDocAutomation] Permission check failed for ${docId}, trying open:`,
        e
      );
      try {
        const { release } = this.docsService.open(docId);
        release();
        return true;
      } catch {
        return false;
      }
    }
  }

  // ===========================================================================
  // Private Helpers
  // ===========================================================================

  /**
   * Opens a document with proper lifecycle management.
   * Handles: open → load → waitForSyncReady
   * Caller MUST call release() when done.
   */
  private async openDocInternal(docId: string): Promise<OpenedDoc | null> {
    let doc, release;
    try {
      const openResult = this.docsService.open(docId);
      doc = openResult.doc;
      release = openResult.release;
    } catch (e) {
      console.warn('[CrossDocAutomation] Failed to open doc:', docId, e);
      return null;
    }

    try {
      const blockSuiteDoc = doc.blockSuiteDoc;
      if (!blockSuiteDoc) {
        release();
        console.warn('[CrossDocAutomation] No blockSuiteDoc for:', docId);
        return null;
      }

      // Ensure doc is fully loaded before querying blocks
      blockSuiteDoc.load();
      await doc.waitForSyncReady();

      return { blockSuiteDoc, release };
    } catch (e) {
      release();
      console.warn('[CrossDocAutomation] Failed to load doc:', docId, e);
      return null;
    }
  }

  /**
   * Finds a database in a BlockSuite doc by ID.
   */
  private findDatabaseInDoc(
    blockSuiteDoc: Store,
    databaseId: string
  ): DatabaseBlockModel | null {
    const databases = blockSuiteDoc.getBlocksByFlavour('affine:database');
    const targetDb = databases.find(
      (db: { id: string }) => db.id === databaseId
    );

    if (!targetDb) return null;

    const model =
      (targetDb as unknown as { model?: DatabaseBlockModel }).model ?? targetDb;
    return model as DatabaseBlockModel;
  }

  /**
   * Extracts column info from a DatabaseBlockDataSource.
   */
  private extractColumns(
    dataSource: DatabaseBlockDataSource
  ): CrossDocColumnInfo[] {
    const propertyIds = dataSource.getBoundedPropertyIdsWindow().propertyIds;
    return propertyIds.map(id => ({
      id,
      name: dataSource.propertyNameGet(id) || id,
      type: dataSource.propertyTypeGet(id) || 'unknown',
    }));
  }

  private async createReadyDataSource(
    model: DatabaseBlockModel
  ): Promise<DatabaseBlockDataSource> {
    if (!(await waitForDatabaseStorageReady(model))) {
      throw new Error(
        `Database storage for "${model.props.databaseId}" did not become ready in root document "${model.props.databaseRootDocId}".`
      );
    }
    return createDatabaseBlockDataSource(model);
  }

  // ===========================================================================
  // Public API
  // ===========================================================================

  /**
   * Get rows from a source database in another document.
   * Used for Input Tab row preview when automation references cross-doc source.
   *
   * @param sourceDocId - Doc ID where source database lives
   * @param sourceTableId - Database block ID
   * @returns Rows with cell data and column metadata
   */
  async getSourceRows(
    sourceDocId: string,
    sourceTableId: string
  ): Promise<GetSourceRowsResult> {
    // 1. Permission Check
    const canRead = await this.canReadDoc(sourceDocId);
    if (!canRead) {
      const error = new PermissionDeniedError(sourceDocId);
      console.warn('[CrossDocAutomation] Permission denied:', error.message);
      return { success: false, error };
    }

    // 2. Open Doc
    const opened = await this.openDocInternal(sourceDocId);
    if (!opened) {
      return {
        success: false,
        error: new Error(`Failed to open document: ${sourceDocId}`),
      };
    }

    const { blockSuiteDoc, release } = opened;

    try {
      // 3. Find Database
      const model = this.findDatabaseInDoc(blockSuiteDoc, sourceTableId);
      if (!model) {
        throw new DatabaseNotFoundError(sourceTableId, sourceDocId);
      }

      // 4. Extract Data
      const dataSource = await this.createReadyDataSource(model);
      const columns = this.extractColumns(dataSource);

      const rowIds = dataSource.getBoundedRowsWindow().rowIds;
      const propertyIds = dataSource.getBoundedPropertyIdsWindow().propertyIds;

      const rows: CrossDocRowData[] = rowIds.map(rowId => {
        const cells: Record<string, unknown> = {};
        for (const propId of propertyIds) {
          cells[propId] = dataSource.cellValueGet(rowId, propId);
        }
        return { rowId, cells };
      });

      return { success: true, rows, columns };
    } catch (e) {
      console.warn('[CrossDocAutomation] Failed to get source rows:', e);
      return {
        success: false,
        error: e instanceof Error ? e : new Error(String(e)),
      };
    } finally {
      release();
    }
  }

  /**
   * Get columns from a target database in another document.
   * Used for Output Tab column mapping when automation references cross-doc target.
   *
   * @param targetDocId - Doc ID where target database lives
   * @param targetTableId - Database block ID
   * @returns Column metadata for mapping dropdowns
   */
  async getTargetColumns(
    targetDocId: string,
    targetTableId: string
  ): Promise<GetTargetColumnsResult> {
    // 1. Permission Check
    const canRead = await this.canReadDoc(targetDocId);
    if (!canRead) {
      const error = new PermissionDeniedError(targetDocId);
      console.warn('[CrossDocAutomation] Permission denied:', error.message);
      return { success: false, error };
    }

    // 2. Open Doc
    const opened = await this.openDocInternal(targetDocId);
    if (!opened) {
      return {
        success: false,
        error: new Error(`Failed to open document: ${targetDocId}`),
      };
    }

    const { blockSuiteDoc, release } = opened;

    try {
      // 3. Find Database
      const model = this.findDatabaseInDoc(blockSuiteDoc, targetTableId);
      if (!model) {
        throw new DatabaseNotFoundError(targetTableId, targetDocId);
      }

      // 4. Extract Columns
      const dataSource = await this.createReadyDataSource(model);
      const columns = this.extractColumns(dataSource);

      console.log('[CrossDocAutomation] Got target columns:', {
        targetDocId,
        targetTableId,
        columnCount: columns.length,
      });

      return { success: true, columns };
    } catch (e) {
      console.warn('[CrossDocAutomation] Failed to get target columns:', e);
      return {
        success: false,
        error: e instanceof Error ? e : new Error(String(e)),
      };
    } finally {
      release();
    }
  }

  /**
   * Get the BlockSuite doc for a given docId.
   * Used when components need direct access to cross-doc BlockSuite operations.
   *
   * IMPORTANT: Caller MUST call release() when done!
   *
   * @param docId - Doc ID to open
   * @returns BlockSuite doc and release function, or null if permission denied
   */
  async openDoc(docId: string): Promise<{
    blockSuiteDoc: Store;
    release: () => void;
  } | null> {
    // Permission Check
    const canRead = await this.canReadDoc(docId);
    if (!canRead) {
      console.warn(
        '[CrossDocAutomation] Permission denied for openDoc:',
        docId
      );
      return null;
    }

    return this.openDocInternal(docId);
  }

  /**
   * Find a database in a document by block ID.
   * Used for cross-doc database lookups.
   *
   * @param docId - Doc ID to search in
   * @param databaseId - Database block ID to find
   * @returns Database model or null
   */
  async findDatabase(
    docId: string,
    databaseId: string
  ): Promise<DatabaseBlockModel | null> {
    // Permission Check
    const canRead = await this.canReadDoc(docId);
    if (!canRead) {
      console.warn(
        '[CrossDocAutomation] Permission denied for findDatabase:',
        docId
      );
      return null;
    }

    const opened = await this.openDocInternal(docId);
    if (!opened) return null;

    const { blockSuiteDoc, release } = opened;

    try {
      return this.findDatabaseInDoc(blockSuiteDoc, databaseId);
    } finally {
      release();
    }
  }
}
